"use client";

import { motion, useReducedMotion } from "motion/react";
import { ArrowRight } from "@phosphor-icons/react";
import { ButtonLink } from "@/components/ui/Button";
import { CastingSheet } from "@/components/sections/CastingSheet";
import { homepageBusinessMetrics } from "@/features/marketplace/business-metrics";

/**
 * Hero — CASTING SHEET（选角样张）。
 * 观念：整屏是一张选角照片墙，巨型 editorial 排版叠压其上；
 * 一墙候选里一个被选中，裂变成千万角色。呼应「一个真实，万千映像」。
 * 光谱散射（彩虹）是核心视觉语言：一束白光散成整条光谱 = 一个真人 → 万千映像。
 */
export function Hero({ navigationPrefetch }: { navigationPrefetch?: false } = {}) {
  const reduce = useReducedMotion();
  const rise = (delay: number) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] as const },
        };

  return (
    <section className="dot-grid relative min-h-[100dvh] overflow-hidden">
      {/* 选角照片墙（视觉主体，铺满；在网格纹理之上）*/}
      <div className="absolute inset-0 z-[1]">
        <CastingSheet />
      </div>

      {/* 石墨可读性罩：左侧压暗，保证叠压其上的排版清晰 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(100deg, var(--color-ink) 0%, color-mix(in oklab, var(--color-ink) 82%, transparent) 34%, transparent 62%)",
        }}
      />
      {/* 底缘向页面背景过渡 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-48"
        style={{ background: "linear-gradient(180deg, transparent, var(--color-ink))" }}
      />

      {/* 排版层（容器不吃指针事件，否则会盖住照片墙拦截 hover；仅按钮等子元素恢复可点）*/}
      <div className="pointer-events-none relative z-10 mx-auto flex min-h-[100dvh] max-w-[1400px] flex-col justify-center px-5 pt-24 pb-28 sm:px-8">
        <div className="pointer-events-none max-w-3xl">
          <motion.p
            {...rise(0)}
            className="font-mono text-sm uppercase tracking-[0.22em] text-[var(--color-mist)]"
          >
            数字形象协作平台 · 产品原型
          </motion.p>

          <motion.h1
            {...rise(0.08)}
            className="text-display mt-5 text-[clamp(3rem,9vw,6.6rem)] font-semibold leading-[0.92] tracking-[-0.03em] text-[var(--color-paper)]"
          >
            Mirafans，<br />
            <span className="text-spectrum">遇见你的另一种可能。</span>
          </motion.h1>

          <motion.p
            {...rise(0.18)}
            className="mt-7 max-w-lg text-lg leading-relaxed text-[var(--color-mist)]"
          >
            创建可审核的数字形象资料，协商授权范围并完成受管交付。链号、DCI 和外部监测能力当前均为明确标注的演示字段。
          </motion.p>

          <motion.div
            {...rise(0.28)}
            className="pointer-events-auto mt-9 flex flex-wrap items-center gap-3"
          >
            <ButtonLink prefetch={navigationPrefetch} href="/register?kind=creator" size="lg">
              我是授权方，实名入驻
            </ButtonLink>
            <ButtonLink prefetch={navigationPrefetch} href="/plaza" variant="secondary" size="lg">
              我要选角
              <ArrowRight size={17} />
            </ButtonLink>
          </motion.div>

          {/* 数据条：磨砂玻璃面板，从网格中「浮」出，竖线分隔，信息分层清晰 */}
          <motion.dl
            {...rise(0.4)}
            className="edge-top glass-panel pointer-events-auto relative mt-12 inline-flex flex-wrap overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-line-strong)]"
            style={{ boxShadow: "var(--shadow-node)" }}
          >
            {homepageBusinessMetrics.map(({ value: num, label }, i) => (
              <div
                key={label}
                className={`px-6 py-4 ${
                  i > 0 ? "border-l border-[var(--color-line)]" : ""
                }`}
              >
                <dt className="tnum text-2xl font-semibold text-[var(--color-paper)]">{num}</dt>
                <dd className="mt-1 text-xs text-[var(--color-faint)]">{label}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
      </div>
    </section>
  );
}
